import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Ban, CircleCheck } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { toast } from "sonner";

type ReadingRow = {
  _id?: string;
  invalid?: boolean;
  [key: string]: unknown;
};

type MoreAction = {
  label: string;
  icon: ReactNode;
  isShow: (row?: ReadingRow) => boolean;
  isDisabled?: (row?: ReadingRow) => boolean;
  onClick: (row: ReadingRow) => void;
};

export function useMarkReadingInvalid({
  enabled,
  onSuccess,
}: {
  enabled: boolean;
  onSuccess?: () => void;
}) {
  const [selected, setSelected] = useState<ReadingRow | null>(null);
  const [invalidReason, setInvalidReason] = useState("");
  const [loading, setLoading] = useState(false);

  const markingInvalid = selected ? !selected.invalid : true;
  const trimmedReason = invalidReason.trim();
  const canConfirm = markingInvalid ? trimmedReason.length > 0 : true;

  const openDialog = useCallback((row: ReadingRow) => {
    setInvalidReason("");
    setSelected(row);
  }, []);

  const closeDialog = useCallback(() => {
    if (loading) return;
    setSelected(null);
    setInvalidReason("");
  }, [loading]);

  const moreActions = useMemo<MoreAction[]>(() => {
    if (!enabled) return [];
    return [
      {
        label: "Mark as Invalid",
        icon: <Ban className="h-4 w-4" />,
        isShow: (row) => !row?.invalid,
        isDisabled: () => loading,
        onClick: openDialog,
      },
      // {
      //   label: "Clear Invalid",
      //   icon: <CircleCheck className="h-4 w-4" />,
      //   isShow: (row) => !!row?.invalid,
      //   isDisabled: () => loading,
      //   onClick: openDialog,
      // },
    ];
  }, [enabled, loading, openDialog]);

  const handleConfirm = useCallback(async () => {
    const readingId = String(selected?._id ?? "").trim();
    if (!readingId) {
      toast.error("Reading ID is missing");
      return;
    }

    const nextInvalid = !selected?.invalid;

    if (nextInvalid && !trimmedReason) {
      toast.error("Reason is required");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setLoading(true);
      const payload: { invalid: boolean; reason?: string } = {
        invalid: nextInvalid,
      };
      if (nextInvalid) {
        payload.reason = trimmedReason;
      }
      await AxiosInstance().patch(`/reading/${readingId}/invalid`, payload);
      resolveFn(
        nextInvalid
          ? "Reading marked as invalid"
          : "Invalid status cleared",
      );
      setSelected(null);
      setInvalidReason("");
      onSuccess?.();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ||
        (error as { message?: string })?.message ||
        "Failed to update reading invalid status";
      rejectFn(error);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selected, trimmedReason, onSuccess]);

  const confirmDialog = (
    <AlertDialog
      open={!!selected}
      onOpenChange={(open) => {
        if (!open) closeDialog();
      }}
    >
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {markingInvalid ? "Reason for Invalid" : "Clear Invalid"}
          </AlertDialogTitle>
          {/* <AlertDialogDescription>
            {markingInvalid
              ? "Enter a reason for marking the reading as invalid"
              : "Are you sure you want to clear the invalid status for this reading?"}
          </AlertDialogDescription> */}
        </AlertDialogHeader>

        {markingInvalid ? (
          <div className="space-y-2 py-0">
            <Label htmlFor="invalid-reason">
              Reason<span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="invalid-reason"
              value={invalidReason}
              onChange={(e) => setInvalidReason(e.target.value)}
              placeholder="Enter reason for marking this reading as invalid"
              rows={3}
              disabled={loading}
              className="w-full resize-none"
            />
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={loading || !canConfirm}
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            // className={
            //   markingInvalid
            //     ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
            //     : undefined
            // }
          >
            {loading
              ? "Saving…"
              : markingInvalid
                ? "Mark as Invalid"
                : "Clear Invalid"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { moreActions, confirmDialog };
}
